import { useApi, useLoad } from "@allergy123/api-client/react";
import { Button, Card, Field, Select, Status } from "@allergy123/ui";
import { useState } from "react";
import { CatalogHeader, errorText } from "./common";

export function RecipesPage() {
  const api = useApi();
  const recipes = useLoad((a) => a.admin.catalog.recipes());
  const antigens = useLoad((a) => a.admin.catalog.antigens());
  const [name, setName] = useState("");
  const [mixAntigenId, setMixAntigenId] = useState("");
  const [components, setComponents] = useState([{ antigenId: "", parts: 1 }, { antigenId: "", parts: 1 }]);
  const [msg, setMsg] = useState<{ kind: "info" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const antigenOptions = [{ value: "", label: "Choose an antigen" }, ...(antigens.data?.antigens ?? []).filter((a) => !a.controlKind).map((a) => ({ value: a.id, label: `${a.displayName} (${a.abbreviation})` }))];

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.admin.catalog.createRecipe({ name: name.trim(), mixAntigenId, components });
      setMsg({ kind: "info", text: `Created ${r.name} version ${r.version}. Earlier versions for this antigen are retired.` });
      setName("");
      recipes.reload();
    } catch (err) {
      setMsg({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <CatalogHeader title="Mix recipes" intro="In-house blends a tray position can be filled from. A changed recipe is a new version; the previous one is retired, never edited." />
      {recipes.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load recipes (${errorText(recipes.error)})`} /></div>}
      {antigens.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load antigens (${errorText(antigens.error)})`} /></div>}
      {msg && <div className="mb-6"><Status kind={msg.kind} label={msg.text} /></div>}
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card>
          {recipes.data?.recipes.length === 0 && <p className="text-ink-secondary">No recipes yet.</p>}
          <ul className="grid gap-4">
            {recipes.data?.recipes.map((r) => (
              <li key={r.id}>
                <div className="font-medium">{r.name} · v{r.version} · {r.status}</div>
                <div className="text-small text-ink-secondary">For {r.mixAntigenName}: {r.components.map((c) => `${c.displayName} × ${c.parts}`).join(" + ")}</div>
              </li>
            ))}
          </ul>
        </Card>
        <Card as="section">
          <h2 className="mb-4 text-h3 font-semibold text-anchor">New recipe</h2>
          <form onSubmit={(e) => void create(e)} className="grid gap-4">
            <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
            <Select label="Mix antigen (the tray position's antigen)" value={mixAntigenId} onChange={(e) => setMixAntigenId(e.target.value)} options={antigenOptions} />
            {components.map((c, i) => (
              <div key={i} className="grid grid-cols-[1fr_6rem] gap-2">
                <Select label={`Component ${i + 1}`} value={c.antigenId} onChange={(e) => setComponents((cs) => cs.map((x, j) => (j === i ? { ...x, antigenId: e.target.value } : x)))} options={antigenOptions} />
                <Field label="Parts" type="number" min={1} value={c.parts} onChange={(e) => setComponents((cs) => cs.map((x, j) => (j === i ? { ...x, parts: Math.max(1, Number(e.target.value) || 1) } : x)))} />
              </div>
            ))}
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setComponents((cs) => [...cs, { antigenId: "", parts: 1 }])}>Add component</Button>
              <Button type="submit" disabled={busy || !mixAntigenId || components.some((c) => !c.antigenId) || name.trim().length < 2}>Create recipe</Button>
            </div>
          </form>
        </Card>
      </div>
    </>
  );
}
